// EditSelfCompliance.jsx
import React, { useState, useEffect, useMemo} from "react";
import { useNavigate } from "react-router-dom";
import UserSidebar from "./UserSidebar";
import UserHeader from "./UserHeader";
import { apiFetch, BASE_URL } from "../api_call";
import { IoIosInformationCircle } from "react-icons/io";
import "./Dashboard.css";

export default function EditSelfCompliance() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [compliances, setCompliances] = useState([]);
  const [files, setFiles] = useState({});
  const [successMsg, setSuccessMsg] = useState("");
  const [successVisible, setSuccessVisible] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();

  // ---------------- Fetch Compliance Instances ----------------
  useEffect(() => {
    const complianceId = localStorage.getItem("selected_compliance_id");
    if (!complianceId) return;

    const fetchComplianceInstances = async () => {
      try {
        const res = await apiFetch(`/compliance/fetch/custom/${complianceId}`, { method: "GET" });
        const records = res?.data || [];

        const formatted = records.map((row) => ({
          slfcmp_id: row.slfcmp_id,
          compliance: row.slfcmp_title || row.slfcmp_particular || row.slfcmp_act,
          start_date: row.slfcmp_start_date,
          action_date: row.slfcmp_action_date,
          end_date: row.slfcmp_end_date,
          reminder: `${row.slfcmp_reminder_days ?? 0} Days`,
          status: row.slfcmp_status || "Pending",
          approver_email: row.slfcmp_approvers_email || "", // prefill if present
          long_description: row.slfcmp_long_description || "",
        }));

        setCompliances(formatted);
      } catch (err) {
        console.error("Failed to fetch self compliance instances:", err);
        setErrorMsg("❌ Failed to fetch compliances");
      }
    };

    fetchComplianceInstances();
  }, []);

  // ---------------- Approver Email Change ----------------
  const handleApproverChange = (index, value) => {
    setCompliances((prev) => {
      const updated = [...prev];
      updated[index].approver_email = value;
      return updated;
    });
  };

  // ---------------- Send to Approver ----------------
  const handleSendToApprover = async (item, index) => {
    if (!item.approver_email) {
      alert("Enter approver email");
      return;
    }

    const formData = new FormData();
    formData.append("approver_email", item.approver_email);
    formData.append("compliance_instance_id", item.slfcmp_id);
    if (files[index]) formData.append("attachment", files[index]);

    try {
      const res = await fetch(`${BASE_URL}/compliance/custom/send_to_approver`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      const data = await res.json();
      if (res.status !== 200) throw data;

      setSuccessMsg("✅ Email sent and compliance requested successfully");
      setErrorMsg("");

      // Clear uploaded file
      setFiles((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });

      // Update status locally
      setCompliances((prev) =>
        prev.map((c) =>
          c.slfcmp_id === item.slfcmp_id ? { ...c, status: "Requested" } : c
        )
      );
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.error || "❌ Failed to send to approver");
      setSuccessMsg("");
    }
  };

  // ---------------- Edit ----------------
  const handleEdit = (slfcmpId) => {
    localStorage.setItem("slfcmpId", slfcmpId);
    navigate("/final-edit-self");
  };

  // ---------------- Delete All ----------------
const handleDeleteAll = async () => {
  const complianceId = localStorage.getItem("selected_compliance_id");

  if (!complianceId) {
    alert("Custom compliance ID not found");
    return;
  }

  const confirmDelete = window.confirm(
    "Are you sure you want to delete all custom compliance records?"
  );
  if (!confirmDelete) return;

  try {
    const res = await apiFetch(
      `/compliance/delete/custom/${complianceId}`,
      { method: "DELETE" }
    );

    setSuccessMsg(
      `✅ ${res?.instances_deleted || "All"} custom compliance records deleted successfully`
    );
    setErrorMsg("");

    // Clear UI immediately
    setCompliances([]);
  } catch (err) {
    console.error(err);
    setErrorMsg(err?.error || "❌ Failed to delete custom compliance records");
    setSuccessMsg("");
  }
};


  // ---------------- Auto-hide Success ----------------
  useEffect(() => {
    if (successMsg) {
      setSuccessVisible(true);
      const timer = setTimeout(() => setSuccessVisible(false), 4500);
      const removeTimer = setTimeout(() => setSuccessMsg(""), 5000);
      return () => {
        clearTimeout(timer);
        clearTimeout(removeTimer);
      };
    }
  }, [successMsg]);
  const statusCounts = useMemo(() => {
  const counts = {
    total: 0,
    pending: 0,
    requested: 0,
    approved: 0,
  };

  compliances.forEach((item) => {
    counts.total++;

    const status = item.status?.toLowerCase();
    if (status === "pending") counts.pending++;
    else if (status === "requested") counts.requested++;
    else if (status === "approved") counts.approved++;
  });

  return counts;
}, [compliances]);

  return (
    <div className="edit-compliance">
      <UserSidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <UserHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

        <main style={{ flex: 1, padding: "1rem 2rem", overflowY: "auto" }}>
          <header>
            <h2>Custom Instances</h2>
          </header>

          {/* ---------------- Messages ---------------- */}
          <div className="messages-top">
            {errorMsg && <div className="login-error">{errorMsg}</div>}
            {successMsg && (
              <div className={`success-msg ${!successVisible ? "fade-out" : ""}`}>
                {successMsg}
              </div>
            )}
          </div>
          <div style={{
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: "10px"
}}>
  <div style={{ display: "flex", gap: "10px" }}>

    <div style={{
      background: "#e2e3e5",
      color: "#383d41",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Total: {statusCounts.total}
    </div>

    <div style={{
      background: "#f8d7da",
      color: "#721c24",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Pending: {statusCounts.pending}
    </div>

    <div style={{
      background: "#fff3cd",
      color: "#856404",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Requested: {statusCounts.requested}
    </div>

    <div style={{
      background: "#d4edda",
      color: "#155724",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Approved: {statusCounts.approved}
    </div>

  </div>
</div>

          {/* ---------------- Table ---------------- */}
          <div className="table-wrapper">
            <table className="data-table" style={{ tableLayout: "fixed", width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: "180px" }}>COMPLIANCE</th>
                  <th style={{ width: "400px" }}>DESCRIPTION</th>
                  <th style={{ width: "100px" }}>START DATE</th>
                  <th style={{ width: "100px" }}>ACTION DATE</th>
                  <th style={{ width: "100px" }}>END DATE</th>
                  <th style={{ width: "90px" }}>REM. BEFORE</th>
                  <th style={{ width: "100px" }}>STATUS</th>
                  <th style={{ width: "120px" }}>APPROVER’S EMAIL</th>
                  <th style={{ width: "140px" }}>ATTACHMENT</th>
                  <th style={{ width: "120px" }}>ACTION</th>
                  <th style={{ width: "60px" }}>EDIT</th>
                </tr>
              </thead>
              <tbody>
                {compliances.length > 0 ? (
                  compliances.map((item, index) => (
                    <tr key={index}>
                     <td>
  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
    
    <span>{item.compliance}</span>

    {item.long_description && (
      <IoIosInformationCircle
        title={item.long_description}
        style={{
          cursor: "pointer",
          fontSize: "16px",
          color: "#1A1744"
        }}
      />
    )}

  </div>
</td>


                       <td style={{ width: "400px", maxWidth: "400px" }}>
  <div className="description-scroll">
    {item.long_description}
  </div>
</td>

                      <td style={{ width: "100px", wordBreak: "break-word" }}>{item.start_date}</td>
                      <td style={{ width: "100px", wordBreak: "break-word" }}>{item.action_date}</td>
                      <td style={{ width: "100px", wordBreak: "break-word" }}>{item.end_date}</td>
                      <td style={{ width: "90px", wordBreak: "break-word" }}>{item.reminder}</td>
                      <td
                        style={{
                          width: "100px",
                          color:
                            item.status.toLowerCase() === "approved"
                              ? "green"
                              : item.status.toLowerCase() === "requested"
                              ? "orange"
                              : "red",
                          fontWeight: 500,
                        }}
                      >
                        {item.status}
                      </td>

                      <td style={{ width: "120px" }}>
                        <input
                          type="email"
                          className="approver-input"
                          value={item.approver_email}
                          onChange={(e) => handleApproverChange(index, e.target.value)}
                          placeholder="Enter email"
                        />
                      </td>

                      <td style={{ width: "140px" }}>
                        <label className="file-upload-label">
                          <input
                            type="file"
                            hidden
                            onChange={(e) =>
                              setFiles((prev) => ({ ...prev, [index]: e.target.files[0] }))
                            }
                          />
                          <span className="custom-file-btn">
                            {files[index] ? files[index].name : "Attach Evidence"}
                          </span>
                        </label>
                      </td>

                      <td style={{ width: "120px" }}>
                        <button
                          className="action-btn primary"
                          onClick={() => handleSendToApprover(item, index)}
                          title="Send to approver"
                        >
                          ✔
                        </button>
                      </td>

                      <td style={{ width: "60px" }}>
                        <button
                          className="action-btn edit"
                          onClick={() => handleEdit(item.slfcmp_id)}
                          disabled={item.status.toLowerCase() === "approved" || item.status.toLowerCase() === "requested"}
                          style={{
                            cursor:
                              item.status.toLowerCase() === "approved" || item.status.toLowerCase() === "requested"
                                ? "not-allowed"
                                : "pointer",
                            opacity:
                              item.status.toLowerCase() === "approved" || item.status.toLowerCase() === "requested" ? 0.5 : 1,
                          }}
                        >
                          ✎
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" style={{ textAlign: "center" }}>
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ textAlign: "center", marginTop: "25px" }}>
            <button
  className="submit-btn"
  style={{ width: "95%" }}
  onClick={handleDeleteAll}
>
  Delete All
</button>

          </div>
        </main>
      </div>
    </div>
  );
}
